import { productSlug, type Product } from "@/stores/app-store"

export function evaluationAnalyticsPath(product: Product, termId: string): string {
  return `/${productSlug(product)}/analytics/${termId}`
}

export function evaluationDashboardPath(product: Product): string {
  return `/${productSlug(product)}/dashboard`
}

export function isEvaluationDashboardPath(pathname: string, product: Product): boolean {
  const base = evaluationDashboardPath(product)
  return pathname === base || pathname.startsWith(`${base}/`)
}

/** Directory courses list (Case 2 — Add courses). */
export function prismCoursesPath(): string {
  return "/courses"
}

export function evaluationTermsPath(product: Product): string {
  return `/${productSlug(product)}/terms`
}

export function evaluationTermSetupPath(product: Product): string {
  return `/${productSlug(product)}/terms/setup`
}

export function evaluationSchedulePath(
  product: Product,
  options?: { termId?: string; stages?: string },
): string {
  const base = `/${productSlug(product)}/evaluations/schedule`
  if (!options?.termId && !options?.stages) return base
  const params = new URLSearchParams()
  if (options.termId) params.set("termId", options.termId)
  if (options.stages) params.set("stages", options.stages)
  const query = params.toString()
  return query ? `${base}?${query}` : base
}

export function evaluationDistributionPath(
  product: Product,
  options?: { termId?: string; action?: "update" | "delete" | "remind" | "extend" },
): string {
  const base = `/${productSlug(product)}/evaluations/distribute`
  const params = new URLSearchParams({ step: "4" })
  if (options?.termId) params.set("termId", options.termId)
  if (options?.action) params.set("action", options.action)
  return `${base}?${params.toString()}`
}

export function evaluationReviewPath(product: Product, termId: string): string {
  return `/${productSlug(product)}/evaluations/review?termId=${encodeURIComponent(termId)}`
}

export function evaluationTermSurveysPath(product: Product, termId: string): string {
  return `/${productSlug(product)}/terms/${encodeURIComponent(termId)}/surveys`
}

export function evaluationTermDetailsPath(product: Product, termId: string): string {
  return `/${productSlug(product)}/terms/${encodeURIComponent(termId)}`
}

/** Stage tile on Operations V2 lifecycle. One href per stage so the hover trigger is a real link. */
export function evaluationLifecycleStagePath(
  product: Product,
  termId: string,
  stage: "notSetup" | "draft" | "scheduled" | "live" | "closed" | "published",
): string {
  switch (stage) {
    case "notSetup":
      return evaluationSchedulePath(product, { termId, stages: "notSetup" })
    case "draft":
      return evaluationSchedulePath(product, { termId, stages: "draft" })
    case "scheduled":
      return evaluationDistributionPath(product, { termId, action: "update" })
    case "live":
      return evaluationDistributionPath(product, { termId, action: "remind" })
    case "closed":
      return evaluationReviewPath(product, termId)
    case "published":
      return evaluationTermSurveysPath(product, termId)
  }
}

export function evaluationTemplatesPath(product: Product): string {
  return `/${productSlug(product)}/templates`
}

export function workQueueActionPath(
  product: Product,
  row: { id: string; state: string },
): string {
  const termId = row.id.replace(
    /-(setup|draft|scheduled|live|closed|publish|attention)$/,
    "",
  )

  switch (row.state) {
    case "notSetup":
    case "draft":
      return evaluationSchedulePath(product, { termId, stages: "notSetup,draft" })
    case "scheduled":
      return evaluationDistributionPath(product, { termId, action: "update" })
    case "live":
      return evaluationDistributionPath(product, { termId, action: "remind" })
    case "closed":
    case "review":
      return evaluationReviewPath(product, termId)
    case "attention":
      return evaluationAnalyticsPath(product, termId)
    default:
      return evaluationDashboardPath(product)
  }
}
